import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { Markdown } from '@/components/ui/markdown';
import { dpaService, type DpaStatus } from '../../services/dpa';
import { useCurrentLocation } from '../hooks/useCurrentLocation';

import dpaEn from '../../locales/legal/dpa.en.md?raw';
import dpaIt from '../../locales/legal/dpa.it.md?raw';

/**
 * Where a gym signs the Article 28 agreement covering its members' data, or a
 * circuit the one covering the people entered in its stages.
 *
 * The form asks for the signatory's legal identity rather than reusing the
 * profile: `locations` holds a street address so climbers can find the place, not
 * a registered office, a VAT number or a legal representative — and a contract
 * records who signed it, on the day, under that name.
 *
 * **The signatory is not assumed to be a gym.** A circuit is organised by a club,
 * a committee or a federation, which often runs no wall at all, so the copy here
 * asks for the organising body and the agreement itself names the controller by
 * what it manages rather than by having a venue. The two sides of the page differ
 * only in wording — one text, one version, signed per place.
 *
 * The text is bundled, not fetched: an agreement that fails to load is one
 * nobody can be said to have read.
 */
export default function DpaPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { slug, loading: gymLoading, locationType } = useCurrentLocation();

  // The two kinds of place that hold personal data, each naming its signatory in
  // its own words. Anything else cannot reach this page (see nav.ts).
  const copy = locationType === 'trophy' ? 'gymAdmin.dpa.trophy' : 'gymAdmin.dpa.gym';

  const [status, setStatus] = useState<DpaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [legalName, setLegalName] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [taxId, setTaxId] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const language = i18n.language?.split('-')[0] === 'it' ? 'it' : 'en';
  const text = language === 'it' ? dpaIt : dpaEn;

  const load = useCallback(async (gymSlug: string) => {
    setLoading(true);
    try {
      setStatus(await dpaService.status(gymSlug));
    } catch {
      toast({ title: t('gymAdmin.dpa.loadError'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  // Pre-fill from the last signature so re-signing a new version is a read and
  // a click, not a re-typing of details that have not changed.
  useEffect(() => {
    const previous = status?.latest;
    if (!previous) return;

    setLegalName((v) => v || previous.legal_name);
    setLegalAddress((v) => v || previous.legal_address);
    setTaxId((v) => v || previous.tax_id);
    setSignerName((v) => v || previous.signer_name);
    setSignerRole((v) => v || previous.signer_role || '');
  }, [status]);

  const complete =
    legalName.trim() !== '' &&
    legalAddress.trim() !== '' &&
    taxId.trim() !== '' &&
    signerName.trim() !== '' &&
    confirmed;

  async function handleAccept() {
    if (!slug || !complete) return;

    setSaving(true);
    try {
      const next = await dpaService.accept(slug, {
        legal_name: legalName.trim(),
        legal_address: legalAddress.trim(),
        tax_id: taxId.trim(),
        signer_name: signerName.trim(),
        signer_role: signerRole.trim() || undefined,
        locale: language,
      });
      setStatus(next);
      setConfirmed(false);
      toast({ title: t('gymAdmin.dpa.accepted') });
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('gymAdmin.dpa.acceptError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  if (gymLoading || loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const outdated = status?.latest != null && !status.signed;
  // Still served on an older text: nothing is locked, but the new one is
  // offered, and what it adds stays off until it is signed.
  const newVersionAvailable = !!status?.signed && !status.current;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      {status?.signed ? (
        <Card className="border-green-600/40">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-green-600" />
            <div className="space-y-1">
              <CardTitle className="text-base">{t('gymAdmin.dpa.signedTitle')}</CardTitle>
              <CardDescription>
                {t('gymAdmin.dpa.signedBy', {
                  name: status.latest?.signer_name,
                  date: status.latest
                    ? new Date(status.latest.signed_at).toLocaleDateString(i18n.language)
                    : '',
                  version: status.latest?.version,
                })}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <dl className="grid gap-1 sm:grid-cols-[10rem_1fr]">
              <dt className="font-medium text-foreground">{t('gymAdmin.dpa.form.legalName')}</dt>
              <dd>{status.latest?.legal_name}</dd>
              <dt className="font-medium text-foreground">{t('gymAdmin.dpa.form.legalAddress')}</dt>
              <dd>{status.latest?.legal_address}</dd>
              <dt className="font-medium text-foreground">{t('gymAdmin.dpa.form.taxId')}</dt>
              <dd>{status.latest?.tax_id}</dd>
            </dl>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-amber-500/50">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-amber-500" />
            <div className="space-y-1">
              <CardTitle className="text-base">
                {outdated ? t('gymAdmin.dpa.outdatedTitle') : t(`${copy}.requiredTitle`)}
              </CardTitle>
              <CardDescription>
                {outdated
                  ? t(`${copy}.outdatedDescription`, { version: status?.latest?.version })
                  : t(`${copy}.requiredDescription`)}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      )}

      {newVersionAvailable && (
        <Card className="border-amber-500/50" data-testid="dpa-new-version">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-amber-500" />
            <div className="space-y-1">
              <CardTitle className="text-base">{t('gymAdmin.dpa.updateAvailableTitle')}</CardTitle>
              <CardDescription>
                {t('gymAdmin.dpa.updateAvailableDescription', {
                  version: status?.latest?.version,
                  required: status?.required_version,
                })}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {/* Scrollable rather than paginated: the whole text has to be here,
              and a contract split across steps is one people click through. */}
          <div className="max-h-[28rem] overflow-y-auto rounded-md border bg-muted/30 p-4">
            <Markdown className="text-sm leading-relaxed">{text}</Markdown>
          </div>
        </CardContent>
      </Card>

      {!status?.current && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('gymAdmin.dpa.form.title')}</CardTitle>
            <CardDescription>{t(`${copy}.formDescription`)}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="dpa-legal-name">{t('gymAdmin.dpa.form.legalName')}</Label>
                <Input
                  id="dpa-legal-name"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  placeholder={t(`${copy}.legalNamePlaceholder`)}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="dpa-legal-address">{t('gymAdmin.dpa.form.legalAddress')}</Label>
                <Input
                  id="dpa-legal-address"
                  value={legalAddress}
                  onChange={(e) => setLegalAddress(e.target.value)}
                  placeholder={t('gymAdmin.dpa.form.legalAddressPlaceholder')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dpa-tax-id">{t('gymAdmin.dpa.form.taxId')}</Label>
                <Input
                  id="dpa-tax-id"
                  value={taxId}
                  onChange={(e) => setTaxId(e.target.value)}
                  placeholder={t('gymAdmin.dpa.form.taxIdPlaceholder')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dpa-signer-name">{t('gymAdmin.dpa.form.signerName')}</Label>
                <Input
                  id="dpa-signer-name"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder={t(`${copy}.signerNamePlaceholder`)}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="dpa-signer-role">
                  {t('gymAdmin.dpa.form.signerRole')}{' '}
                  <span className="text-muted-foreground">{t('gymAdmin.dpa.form.optional')}</span>
                </Label>
                <Input
                  id="dpa-signer-role"
                  value={signerRole}
                  onChange={(e) => setSignerRole(e.target.value)}
                  placeholder={t(`${copy}.signerRolePlaceholder`)}
                />
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-md border p-3">
              <Checkbox
                id="dpa-confirm"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
                className="mt-0.5"
              />
              <Label htmlFor="dpa-confirm" className="text-sm font-normal leading-relaxed">
                {t('gymAdmin.dpa.form.confirm', { version: status?.required_version })}
              </Label>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleAccept} disabled={!complete || saving}>
                {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('gymAdmin.dpa.form.submit')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
